/**
 * Setup Wizard Hook
 *
 * Orchestrates the first-launch setup wizard flow.
 * Manages navigation between steps, system checks, and use-case-driven
 * component resolution and batch installation.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getSystemCheck,
  getWizardState,
  getWizardSteps,
  updateWizardState,
  completeWizard,
  checkIsFirstLaunch,
  resolveUseCaseDependencies,
  startBatchInstall,
  cancelBatchInstall,
  type SystemCheckResult,
  type WizardState,
  type WizardStepInfo,
  type ResolvedDependencies,
} from '../services/setupService';
import { RETRY_CONFIGS } from '../constants/timings';
import { STORAGE_KEYS } from '../constants/storage';

export type WizardStep =
  | 'welcome'
  | 'use_case_selection'
  | 'system_check'
  | 'disk_space'
  | 'installation_progress'
  | 'completion';

export interface UseSetupWizardReturn {
  // State
  isFirstLaunch: boolean | null;
  isLoading: boolean;
  error: string | null;

  // Wizard state
  currentStep: WizardStep;
  steps: WizardStepInfo[];
  completedSteps: string[];
  isCompleted: boolean;

  // System check
  systemCheck: SystemCheckResult | null;
  isSystemCheckLoading: boolean;
  // Number of automatic GPU-calibration retries attempted so far, and the
  // cap after which the UI should stop auto-retrying and ask the user to
  // click "Re-check" manually.
  gpuCalibrationRetryCount: number;
  maxGpuCalibrationRetries: number;

  // Use cases
  selectedUseCases: string[];
  setSelectedUseCases: (useCases: string[]) => void;

  // Resolved dependencies
  resolvedDependencies: ResolvedDependencies | null;
  isDependencyResolving: boolean;

  // Optional components (opt-in additions to the required set)
  selectedOptionalComponents: string[];
  toggleOptionalComponent: (id: string) => void;

  // Navigation
  goToNextStep: () => Promise<void>;
  goToPreviousStep: () => void;

  // Actions
  runSystemCheck: () => Promise<void>;
  resolveDependencies: () => Promise<void>;
  startInstallation: () => Promise<void>;
  cancelInstallation: () => Promise<void>;
  completeSetup: () => Promise<void>;
  skipWizard: () => Promise<void>;

  // Progress
  currentStepIndex: number;
  totalSteps: number;
  progressPercent: number;
}

const ORDERED_STEPS: WizardStep[] = [
  'welcome',
  'use_case_selection',
  'system_check',
  'disk_space',
  'installation_progress',
  'completion',
];

const { maxRetries: MAX_RETRIES, delayMs: RETRY_DELAY_MS } = RETRY_CONFIGS.PATIENT;
const { maxRetries: MAX_GPU_CALIBRATION_RETRIES } = RETRY_CONFIGS.GPU_CALIBRATION;

export function useSetupWizard(): UseSetupWizardReturn {
  // Core state
  const [isFirstLaunch, setIsFirstLaunch] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard state
  const [wizardState, setWizardState] = useState<WizardState | null>(null);
  const [steps, setSteps] = useState<WizardStepInfo[]>([]);
  const [currentStep, setCurrentStep] = useState<WizardStep>('welcome');

  // System check state
  const [systemCheck, setSystemCheck] = useState<SystemCheckResult | null>(null);
  const [isSystemCheckLoading, setIsSystemCheckLoading] = useState(false);
  // Tracks automatic GPU-calibration retries so the wizard doesn't dump the
  // user into a manual "click Re-check" state after the very first attempt
  // (a cold cache miss, driver JIT warm-up, etc. can fail once and then
  // succeed on retry without any user action being needed).
  const [gpuCalibrationRetryCount, setGpuCalibrationRetryCount] = useState(0);

  // Use case state
  const [selectedUseCases, setSelectedUseCasesState] = useState<string[]>([]);

  // Resolved dependencies state
  const [resolvedDependencies, setResolvedDependencies] = useState<ResolvedDependencies | null>(
    null
  );
  const [isDependencyResolving, setIsDependencyResolving] = useState(false);

  // Optional components the user opted-in to install. Stored as a set of
  // component IDs; resolved metadata comes from resolvedDependencies.optionalComponents.
  const [selectedOptionalComponents, setSelectedOptionalComponents] = useState<string[]>([]);

  // Check if first launch on mount with retry logic
  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;

    const checkFirstLaunch = async (): Promise<void> => {
      if (!isMounted) return;

      setIsLoading(true);
      try {
        // Backend is source of truth - check API first
        const isFirst = await checkIsFirstLaunch();

        if (!isMounted) return;

        // Sync localStorage with backend state
        if (!isFirst) {
          localStorage.setItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED, 'true');
        }

        setIsFirstLaunch(isFirst);

        if (isFirst) {
          // Load wizard state and steps
          const [state, stepsResponse] = await Promise.all([getWizardState(), getWizardSteps()]);

          if (!isMounted) return;

          setWizardState(state);
          setSteps(stepsResponse.steps);

          const savedStep = (state.currentStep || 'welcome') as WizardStep;
          setCurrentStep(savedStep);

          // Restore selected use cases from saved state
          if (state.selectedUseCases && state.selectedUseCases.length > 0) {
            setSelectedUseCasesState(state.selectedUseCases);
          }
        }

        setIsLoading(false);
      } catch (err) {
        console.warn(
          `Failed to check first launch (attempt ${retryCount + 1}/${MAX_RETRIES}):`,
          err
        );

        // Retry if backend not ready yet (likely still starting)
        if (retryCount < MAX_RETRIES - 1) {
          retryCount++;
          setTimeout(() => {
            if (isMounted) {
              checkFirstLaunch();
            }
          }, RETRY_DELAY_MS);
          return;
        }

        // After max retries, check localStorage as fallback
        const localCompleted = localStorage.getItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED);
        if (localCompleted === 'true') {
          setIsFirstLaunch(false);
        } else {
          // Still show wizard if localStorage doesn't say completed
          // This ensures first-time users see the wizard even if backend is slow
          console.log('Backend unavailable, showing wizard based on localStorage');
          setIsFirstLaunch(true);
          setSteps([
            {
              id: 'welcome',
              name: 'Welcome',
              description: '',
              icon: 'sparkles',
              isComponentStep: false,
              componentId: null,
              canSkip: false,
            },
            {
              id: 'completion',
              name: 'Complete',
              description: '',
              icon: 'check-circle',
              isComponentStep: false,
              componentId: null,
              canSkip: false,
            },
          ]);
        }
        setError('Backend connection slow - some features may be limited');
        setIsLoading(false);
      }
    };

    checkFirstLaunch();

    return () => {
      isMounted = false;
    };
  }, []);

  // Get current step index from ordered steps list
  const currentStepIndex = useMemo(() => {
    const idx = ORDERED_STEPS.indexOf(currentStep);
    return idx >= 0 ? idx : 0;
  }, [currentStep]);

  // Total steps
  const totalSteps = ORDERED_STEPS.length;

  // Calculate progress
  const progressPercent = useMemo(() => {
    if (totalSteps === 0) return 0;
    return Math.round((currentStepIndex / (totalSteps - 1)) * 100);
  }, [currentStepIndex, totalSteps]);

  // Set selected use cases and persist to backend state
  const setSelectedUseCases = useCallback(
    (useCases: string[]) => {
      setSelectedUseCasesState(useCases);
      // Invalidate any previously resolved dependencies - they were
      // computed for the OLD selection. goToNextStep() only re-resolves
      // when resolvedDependencies is null (see the guard there), so without
      // this reset, changing use cases after already visiting System Check
      // once would keep showing the stale component/disk list from the
      // earlier selection.
      setResolvedDependencies(null);
      // Persist to backend state - fire and forget
      updateWizardState(currentStep, undefined, useCases).catch((err: unknown) => {
        console.warn('Failed to persist selected use cases:', err);
      });
    },
    [currentStep]
  );

  // Run system check. Resets the GPU-calibration auto-retry counter, since
  // this is called for a fresh check (initial load or an explicit manual
  // "Re-check" click) rather than an automatic follow-up attempt.
  const runSystemCheck = useCallback(async () => {
    setGpuCalibrationRetryCount(0);
    setIsSystemCheckLoading(true);
    setError(null);
    try {
      const result = await getSystemCheck();
      setSystemCheck(result);
    } catch (err) {
      console.error('System check failed:', err);
      setError('Failed to run system check');
    } finally {
      setIsSystemCheckLoading(false);
    }
  }, []);

  // Auto-run system check when step is restored from saved state
  // (goToNextStep only triggers it during navigation, not on state restore)
  useEffect(() => {
    if (currentStep === 'system_check' && !systemCheck && !isSystemCheckLoading && !isLoading) {
      runSystemCheck();
    }
  }, [currentStep, systemCheck, isSystemCheckLoading, isLoading, runSystemCheck]);

  // Auto-retry GPU calibration a bounded number of times before falling back
  // to asking the user to click "Re-check" manually. A single failed
  // attempt (cold cache, transient FFmpeg hiccup) shouldn't require user
  // action - only a *persistent* failure should.
  useEffect(() => {
    if (
      currentStep !== 'system_check' ||
      !systemCheck ||
      isSystemCheckLoading ||
      systemCheck.gpuBenchmarkReady ||
      systemCheck.gpu.vendor !== 'NVIDIA' ||
      gpuCalibrationRetryCount >= MAX_GPU_CALIBRATION_RETRIES
    ) {
      return;
    }

    setGpuCalibrationRetryCount((count) => count + 1);
    setIsSystemCheckLoading(true);
    setError(null);
    getSystemCheck()
      .then(setSystemCheck)
      .catch((err: unknown) => {
        console.error('GPU calibration retry failed:', err);
        setError('Failed to run system check');
      })
      .finally(() => setIsSystemCheckLoading(false));
  }, [currentStep, systemCheck, isSystemCheckLoading, gpuCalibrationRetryCount]);

  // Resolve dependencies for selected use cases
  const resolveDependencies = useCallback(async () => {
    setIsDependencyResolving(true);
    setError(null);
    try {
      const result = await resolveUseCaseDependencies(selectedUseCases);
      setResolvedDependencies(result);
    } catch (err) {
      console.error('Dependency resolution failed:', err);
      setError('Failed to resolve dependencies');
    } finally {
      setIsDependencyResolving(false);
    }
  }, [selectedUseCases]);

  // Auto-run dependency resolution when the disk_space step is restored from
  // saved state (e.g. the wizard was closed mid-setup and reopened later).
  // goToNextStep() only triggers resolveDependencies() during the
  // use_case_selection -> system_check transition, not on state restore, so
  // resuming directly at disk_space left resolvedDependencies stuck at null
  // and the step silently rendered 0.0 GB / "no components needed" from the
  // `?? 0` / `?? []` fallbacks in FirstLaunchWizard/index.tsx instead of the
  // real, already-computed values. Mirrors the system_check auto-run effect
  // above.
  useEffect(() => {
    if (
      currentStep === 'disk_space' &&
      !resolvedDependencies &&
      !isDependencyResolving &&
      !isLoading
    ) {
      resolveDependencies();
    }
  }, [currentStep, resolvedDependencies, isDependencyResolving, isLoading, resolveDependencies]);

  // Toggle an optional component in the opt-in set
  const toggleOptionalComponent = useCallback((id: string) => {
    setSelectedOptionalComponents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  // Drop stale optional selections when use cases change
  useEffect(() => {
    const validIds = new Set((resolvedDependencies?.optionalComponents ?? []).map((c) => c.id));
    setSelectedOptionalComponents((prev) => prev.filter((id) => validIds.has(id)));
  }, [resolvedDependencies]);

  // Start batch installation
  const startInstallation = useCallback(async () => {
    if (!resolvedDependencies) return;

    const requiredIds = resolvedDependencies.components
      .filter((c) => !c.alreadyInstalled)
      .map((c) => c.id);

    const optionalLookup = new Map(resolvedDependencies.optionalComponents.map((c) => [c.id, c]));
    const optionalIds = selectedOptionalComponents.filter((id) => {
      const meta = optionalLookup.get(id);
      return meta ? !meta.alreadyInstalled : false;
    });

    const seen = new Set<string>();
    const componentIds = [...requiredIds, ...optionalIds].filter((id) => {
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    setError(null);
    try {
      await startBatchInstall(componentIds);
    } catch (err) {
      console.error('Failed to start batch installation:', err);
      setError('Failed to start installation');
      throw err;
    }
  }, [resolvedDependencies, selectedOptionalComponents]);

  // Cancel batch installation
  const cancelInstallation = useCallback(async () => {
    setError(null);
    try {
      await cancelBatchInstall();
    } catch (err) {
      console.error('Failed to cancel installation:', err);
      setError('Failed to cancel installation');
    }
  }, []);

  // Navigate to next step
  const goToNextStep = useCallback(async () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= ORDERED_STEPS.length) return;

    const nextStep = ORDERED_STEPS[nextIndex];
    if (!nextStep) return;

    setError(null);
    try {
      // Side effects on specific transitions
      if (nextStep === 'system_check') {
        // Moving from use_case_selection -> system_check (or back-and-forth
        // through the wizard). Only re-fetch if we don't already have fresh
        // results from earlier in this session - the GPU calibration
        // benchmark alone can take 1-2 minutes, and unconditionally
        // re-running it (plus dependency resolution) on every revisit made
        // clicking Back then Continue hang for 5-20s per request with
        // several redundant calls firing at once (visible as concurrent
        // slow-request log lines) before landing on disk_space with a
        // stale "Failed to run system check" error from an abandoned call.
        // The explicit "Re-check" button on the System Check step still
        // forces a fresh run regardless of this guard.
        if (!systemCheck) {
          runSystemCheck();
        }
        if (!resolvedDependencies) {
          resolveDependencies();
        }
      } else if (nextStep === 'installation_progress') {
        // Moving from disk_space -> installation_progress
        await startInstallation();
      }

      await updateWizardState(nextStep, true, selectedUseCases);
      setCurrentStep(nextStep);
    } catch (err) {
      console.error('Failed to go to next step:', err);
      setError('Failed to navigate to next step');
    }
  }, [
    currentStepIndex,
    selectedUseCases,
    systemCheck,
    resolvedDependencies,
    runSystemCheck,
    resolveDependencies,
    startInstallation,
  ]);

  // Navigate to previous step
  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex < 0) return;

    const prevStep = ORDERED_STEPS[prevIndex];
    if (!prevStep) return;

    setCurrentStep(prevStep);

    // Update backend state - fire and forget
    updateWizardState(prevStep).catch(console.error);
  }, [currentStepIndex]);

  // Complete setup
  const completeSetup = useCallback(async () => {
    setError(null);
    try {
      await completeWizard();
      localStorage.setItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED, 'true');
      setIsFirstLaunch(false);
    } catch (err) {
      console.error('Failed to complete setup:', err);
      setError('Failed to complete setup');
      throw err;
    }
  }, []);

  // Skip wizard entirely
  const skipWizard = useCallback(async () => {
    setError(null);
    try {
      await completeWizard();
      localStorage.setItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED, 'true');
      setIsFirstLaunch(false);
    } catch (err) {
      console.error('Failed to skip wizard:', err);
      // Still mark as completed locally even if backend fails
      localStorage.setItem(STORAGE_KEYS.SETUP_WIZARD_COMPLETED, 'true');
      setIsFirstLaunch(false);
    }
  }, []);

  return {
    // State
    isFirstLaunch,
    isLoading,
    error,

    // Wizard state
    currentStep,
    steps,
    completedSteps: wizardState?.completedSteps ?? [],
    isCompleted: wizardState?.isCompleted ?? false,

    // System check
    systemCheck,
    isSystemCheckLoading,
    gpuCalibrationRetryCount,
    maxGpuCalibrationRetries: MAX_GPU_CALIBRATION_RETRIES,

    // Use cases
    selectedUseCases,
    setSelectedUseCases,

    // Resolved dependencies
    resolvedDependencies,
    isDependencyResolving,

    // Optional components
    selectedOptionalComponents,
    toggleOptionalComponent,

    // Navigation
    goToNextStep,
    goToPreviousStep,

    // Actions
    runSystemCheck,
    resolveDependencies,
    startInstallation,
    cancelInstallation,
    completeSetup,
    skipWizard,

    // Progress
    currentStepIndex,
    totalSteps,
    progressPercent,
  };
}
